import type { LiveValue } from '../api/types.js';

/** §8.2: timestamps are UTC; the UI shows Europe/Bratislava unless a site says otherwise. */
export const DEFAULT_TIMEZONE = 'Europe/Bratislava';

export const PLACEHOLDER = '—';

/** Thousands grouped with a narrow no-break space: 43 200. */
/**
 * Siemens description path (property 3121) for display: the levels joined
 * with ›, without the last one when it repeats the description.
 */
export function formatPath(
  path: readonly string[] | null | undefined,
  description?: string | null,
): string {
  if (!path?.length) return '';
  const levels = path.at(-1) === description ? path.slice(0, -1) : path;
  return levels.join(' › ');
}

/** The Siemens path of an object or point: property 3121 or 4397, whichever it has. */
export function siemensPath(o: {
  siemens_3121_path?: string[] | null | undefined;
  siemens_4397_path?: string[] | null | undefined;
}): string[] | null {
  return o.siemens_3121_path ?? o.siemens_4397_path ?? null;
}

export function formatCount(n: number): string {
  const s = Math.round(n).toString();
  return s.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

/** 1–3 decimals depending on magnitude: 1234.5, 21.37, 0.425. */
export function floatDecimals(v: number): number {
  const a = Math.abs(v);
  if (a >= 100) return 1;
  if (a >= 1) return 2;
  return 3;
}

export function formatFloat(v: number): string {
  if (!Number.isFinite(v)) return String(v);
  const s = v.toFixed(floatDecimals(v));
  return s === '-0.000' || s === '-0.00' || s === '-0.0' ? s.slice(1) : s;
}

/** A value of the given Sparkplug datatype, without its unit. */
export function formatTypedValue(
  dataType: string,
  v: number | null,
  vs: string | null = null,
): string {
  if (v === null) return vs ?? PLACEHOLDER;
  switch (dataType) {
    case 'Boolean':
      return v !== 0 ? 'On' : 'Off';
    case 'UInt32':
    case 'UInt8':
    case 'UInt16':
    case 'Int32':
    case 'Int64':
    case 'UInt64':
      return Math.round(v).toString();
    case 'Float':
    case 'Double':
      return formatFloat(v);
    default:
      return Number.isInteger(v) ? v.toString() : formatFloat(v);
  }
}

/** The live value as shown in tables: "21.37 °C", "On", "3". */
export function formatLiveValue(
  dataType: string,
  unit: string,
  live: Pick<LiveValue, 'v' | 'vs'> | null | undefined,
): string {
  if (!live) return PLACEHOLDER;
  const text = formatTypedValue(dataType, live.v, live.vs);
  if (live.v === null || dataType === 'Boolean' || !unit || unit === 'no-units') return text;
  return `${text} ${unit}`;
}

export type Quality = 'good' | 'uncertain' | 'bad';

/** OPC-style quality codes: 192 good, 64 uncertain, 0 bad. */
export function qualityOf(q: number): Quality {
  if (q >= 192) return 'good';
  if (q >= 64) return 'uncertain';
  return 'bad';
}

export interface StatusFlag {
  bit: number;
  key: 'alarm' | 'fault' | 'overridden' | 'oos';
  letter: string;
  label: string;
}

/** BACnet StatusFlags in the order of the bit string. */
export const STATUS_FLAGS: readonly StatusFlag[] = [
  { bit: 1, key: 'alarm', letter: 'A', label: 'In alarm' },
  { bit: 2, key: 'fault', letter: 'F', label: 'Fault' },
  { bit: 4, key: 'overridden', letter: 'O', label: 'Overridden' },
  { bit: 8, key: 'oos', letter: 'S', label: 'Out of service' },
];

export function statusFlagsOf(sf: number | null | undefined): StatusFlag[] {
  if (!sf) return [];
  return STATUS_FLAGS.filter((f) => (sf & f.bit) !== 0);
}

/** "just now", "12 s ago", "5 min ago", "3 h ago", "4 d ago". */
export function formatAge(ts: number | null | undefined, now: number): string {
  if (ts === null || ts === undefined) return 'never';
  const s = Math.floor((now - ts) / 1000);
  if (s < 1) return 'just now';
  if (s < 60) return `${s} s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.floor(h / 24)} d ago`;
}

/** Compact duration from seconds: "45 s", "12 min", "5 h 3 min", "3 d 4 h". */
export function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return m % 60 ? `${h} h ${m % 60} min` : `${h} h`;
  const d = Math.floor(h / 24);
  return h % 24 ? `${d} d ${h % 24} h` : `${d} d`;
}

/** Megabytes as "512 MB" or "12.3 GB". */
export function formatMegabytes(mb: number): string {
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${Math.round(mb)} MB`;
}

/** Elapsed milliseconds as "340 ms" or "1.2 s". */
export function formatElapsed(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

const dateTimeFormats = new Map<string, Intl.DateTimeFormat>();

function dateTimeFormat(timeZone: string): Intl.DateTimeFormat {
  let f = dateTimeFormats.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });
    dateTimeFormats.set(timeZone, f);
  }
  return f;
}

/** "2026-10-25 14:03:07" in the given time zone. */
export function formatTimestamp(ts: number, timeZone: string = DEFAULT_TIMEZONE): string {
  const parts: Record<string, string> = {};
  for (const p of dateTimeFormat(timeZone).formatToParts(ts)) parts[p.type] = p.value;
  return `${parts.year ?? ''}-${parts.month ?? ''}-${parts.day ?? ''} ${parts.hour ?? ''}:${parts.minute ?? ''}:${parts.second ?? ''}`;
}
